import React, { useEffect, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useGetDipendenti } from "../gestversore/Services";
import { Dipendenti } from "../gestversore/types";
import {
  useCreateRisorsaCalDav,
  useGetRisorsaCalDavById,
  useUpdateRisorsaCalDav,
} from "./Services";
import { RisorsaCalDav } from "./types";

type Props = {
  id?: number;
  onClose: () => void;
};

type RisorsaCalDavForm = {
  id?: number;
  coddip: string;
  caldavUrl: string;
  username: string;
  passwordApp?: string;
  enabled?: boolean;
};

const schema = Yup.object().shape({
  coddip: Yup.string().required("Risorsa obbligatoria"),
  caldavUrl: Yup.string().url("URL non valido").required("URL CalDAV obbligatorio"),
  username: Yup.string().required("Username obbligatorio"),
  passwordApp: Yup.string(),
  enabled: Yup.boolean(),
});

const filterDipendenti = createFilterOptions<Dipendenti>({
  stringify: (option) =>
    [
      option.coddip,
      option.nomedip,
      option.username,
      option.label,
    ]
      .filter(Boolean)
      .join(" "),
});

const RisorsaCalDavDialog: React.FC<Props> = ({ id, onClose }) => {
  const isEdit = Boolean(id);
  const { data } = useGetRisorsaCalDavById(id ?? 0);
  const { data: dipendenti } = useGetDipendenti(0, "");
  const { mutateAsync: createItem, isPending: isCreating } = useCreateRisorsaCalDav();
  const { mutateAsync: updateItem, isPending: isUpdating } = useUpdateRisorsaCalDav();

  const dipendenteOptions = useMemo(() => {
    const map = new Map<string, Dipendenti>();

    (dipendenti?.results ?? []).forEach((item) => {
      const coddip = item.coddip?.trim();
      if (coddip && !map.has(coddip)) {
        map.set(coddip, {
          ...item,
          coddip,
          label: `${item.nomedip ?? item.label ?? coddip} (${coddip})`,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a.label ?? "").localeCompare(b.label ?? "")
    );
  }, [dipendenti]);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<RisorsaCalDavForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      coddip: "",
      caldavUrl: "",
      username: "",
      passwordApp: "",
      enabled: true,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        id: data.id,
        coddip: data.coddip ?? "",
        caldavUrl: data.caldavUrl ?? "",
        username: data.username ?? "",
        passwordApp: "",
        enabled: data.enabled ?? true,
      });
    }
  }, [data, reset]);

  const onSubmit: SubmitHandler<RisorsaCalDavForm> = async (values) => {
    const payload: RisorsaCalDav = {
      ...values,
      id,
      passwordApp: values.passwordApp?.trim() ? values.passwordApp : undefined,
    };

    if (isEdit) {
      await updateItem(payload);
    } else {
      await createItem(payload);
    }

    onClose();
  };

  const isSaving = isCreating || isUpdating;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="h5">{isEdit ? "Modifica CalDAV Risorsa" : "Nuova CalDAV Risorsa"}</Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Controller
              name="coddip"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={dipendenteOptions}
                  filterOptions={filterDipendenti}
                  getOptionLabel={(option) => option.label ?? option.coddip ?? ""}
                  isOptionEqualToValue={(option, value) => option.coddip === value.coddip}
                  value={dipendenteOptions.find((item) => item.coddip === field.value) ?? null}
                  onChange={(_, value) => field.onChange(value?.coddip ?? "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Risorsa"
                      error={!!errors.coddip}
                      helperText={errors.coddip?.message}
                    />
                  )}
                />
              )}
            />

            <Controller
              name="caldavUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="URL CalDAV"
                  placeholder="https://nc.domain.it/remote.php/dav/calendars/utente/condiviso/"
                  error={!!errors.caldavUrl}
                  helperText={errors.caldavUrl?.message}
                  fullWidth
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username Nextcloud"
                    error={!!errors.username}
                    helperText={errors.username?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="passwordApp"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={isEdit ? "Nuova password app" : "Password app"}
                    type="password"
                    helperText={isEdit ? "Lascia vuoto per mantenere quella salvata" : ""}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={Boolean(field.value)} onChange={(event) => field.onChange(event.target.checked)} />}
                  label="Sincronizzazione abilitata"
                />
              )}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>CHIUDI</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
              >
                SALVA
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RisorsaCalDavDialog;
